import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import {
  Clock,
  Check,
  Archive,
  ListChecks,
  RotateCcw,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';

// Reporting DB client (same config as reportingSupabase.ts, but self-contained
// to avoid modifying the existing file's exports)
const REPORTING_SUPABASE_URL = import.meta.env.VITE_REPORTING_SUPABASE_URL || 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY = import.meta.env.VITE_REPORTING_SUPABASE_ANON_KEY || 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';
const reportingSupabase = createClient(REPORTING_SUPABASE_URL, REPORTING_SUPABASE_ANON_KEY);

interface ReportTodo {
  id: string;
  report_id: string;
  status: 'pending' | 'done' | 'final';
  last_reset_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ReportRow {
  id: string;
  name: string;
  created_at: string;
  status: string;
  platforms: string[];
  report_type: string | null;
  share_url: string | null;
}

type TodoStatus = 'pending' | 'done' | 'final';

// --- Sunday reset helpers ---
function lastSundayUTC(): Date {
  const now = new Date();
  const day = now.getUTCDay();
  const sunday = new Date(now);
  sunday.setUTCDate(now.getUTCDate() - day);
  sunday.setUTCHours(0, 0, 0, 0);
  return sunday;
}

function shouldReset(lastResetAt: string | null): boolean {
  if (!lastResetAt) return true;
  return new Date(lastResetAt) < lastSundayUTC();
}

const REPORTING_TOOL_URL = 'https://gleaming-macaron-d12189.netlify.app';

const ReportingList: React.FC<{ searchQuery?: string }> = ({ searchQuery = '' }) => {
  const [loading, setLoading] = useState(true);
  const [activeTodos, setActiveTodos] = useState<(ReportTodo & { report: ReportRow })[]>([]);
  const [finalTodos, setFinalTodos] = useState<(ReportTodo & { report: ReportRow })[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  // Campaign type filter. Reports created before report_type existed have it
  // null; those are treated as 'release', matching typeBadge's behaviour.
  const [typeFilter, setTypeFilter] = useState<'all' | 'release' | 'tour' | 'follower'>('all');
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const matchesSearch = (todo: ReportTodo & { report: ReportRow }) =>
    !normalizedSearch || todo.report.name.toLowerCase().includes(normalizedSearch);
  const matchesType = (todo: ReportTodo & { report: ReportRow }) =>
    typeFilter === 'all' || (todo.report.report_type || 'release') === typeFilter;
  const matchesFilters = (todo: ReportTodo & { report: ReportRow }) =>
    matchesSearch(todo) && matchesType(todo);
  const visibleActiveTodos = activeTodos.filter(matchesFilters);
  const visibleFinalTodos = finalTodos.filter(matchesFilters);

  // Counts per type, open todos only — finished reports pile up over time
  // (hundreds of old releases) and would drown out what's actually left to do.
  // Search is applied, the type filter itself is not, so the chips show what's
  // behind them before you click.
  const typeCount = (type: 'all' | 'release' | 'tour' | 'follower') =>
    activeTodos
      .filter(matchesSearch)
      .filter((t) => type === 'all' || (t.report.report_type || 'release') === type).length;

  const loadTodos = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch all reports from reporting DB
      const { data: reports, error: repError } = await reportingSupabase
        .from('reports')
        .select('id, name, created_at, status, platforms, report_type, share_url')
        .order('created_at', { ascending: false });
      if (repError) throw repError;

      // 2. Fetch all report_todos
      const { data: todos, error: todoError } = await reportingSupabase
        .from('report_todos')
        .select('*');
      if (todoError) throw todoError;

      const todoMap = new Map<string, ReportTodo>();
      ((todos || []) as ReportTodo[]).forEach((t) => todoMap.set(t.report_id, t));

      // 3. Auto-sync: create todo entries for reports that don't have one yet
      const missing = (reports || []).filter((r: ReportRow) => !todoMap.has(r.id));
      if (missing.length > 0) {
        const inserts = missing.map((r: ReportRow) => ({
          report_id: r.id,
          status: 'pending' as TodoStatus,
          last_reset_at: new Date().toISOString(),
        }));
        const { data: inserted, error: insError } = await reportingSupabase
          .from('report_todos')
          .insert(inserts)
          .select('*');
        if (insError) throw insError;
        ((inserted || []) as ReportTodo[]).forEach((t) => todoMap.set(t.report_id, t));
      }

      // 4. Sunday reset: reset all non-final entries to pending if a new Sunday has passed
      const sunday = lastSundayUTC();
      const toReset = (reports || [])
        .map((r: ReportRow) => todoMap.get(r.id))
        .filter((t): t is ReportTodo => !!t && t.status !== 'final' && shouldReset(t.last_reset_at));

      if (toReset.length > 0) {
        const resetIds = toReset.map((t) => t.id);
        const { error: resetError } = await reportingSupabase
          .from('report_todos')
          .update({ status: 'pending', last_reset_at: sunday.toISOString(), updated_at: new Date().toISOString() })
          .in('id', resetIds);
        if (resetError) throw resetError;
        toReset.forEach((t) => {
          const updated = { ...t, status: 'pending' as TodoStatus, last_reset_at: sunday.toISOString() };
          todoMap.set(t.report_id, updated);
        });
      }

      // 5. Merge + split into active / final
      const merged = (reports || [])
        .map((r: ReportRow) => {
          const t = todoMap.get(r.id);
          if (!t) return null;
          return { ...t, report: r };
        })
        .filter((x): x is ReportTodo & { report: ReportRow } => x !== null);

      const active = merged
        .filter((t) => t.status !== 'final')
        .sort((a, b) => {
          const order = (s: TodoStatus) => (s === 'pending' ? 0 : 1);
          if (order(a.status) !== order(b.status)) return order(a.status) - order(b.status);
          return new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime();
        });

      const final = merged
        .filter((t) => t.status === 'final')
        .sort((a, b) => new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime());

      setActiveTodos(active);
      setFinalTodos(final);
    } catch (e: any) {
      console.error('ReportingList load error:', e);
      toast.error('Fehler beim Laden der Reporting-Liste');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    if (openMenuId) {
      window.addEventListener('click', handler);
      return () => window.removeEventListener('click', handler);
    }
  }, [openMenuId]);

  const updateStatus = async (todoId: string, status: TodoStatus) => {
    try {
      const { error } = await reportingSupabase
        .from('report_todos')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', todoId);
      if (error) throw error;
      setOpenMenuId(null);
      patchTodoStatus(todoId, status);
    } catch (e: any) {
      console.error('Update error:', e);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const bulkUpdate = async (status: TodoStatus) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      const { error } = await reportingSupabase
        .from('report_todos')
        .update({ status, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      ids.forEach((id) => patchTodoStatus(id, status));
      setSelectedIds(new Set());
      toast.success(`${ids.length} Einträge aktualisiert`);
    } catch (e: any) {
      console.error('Bulk update error:', e);
      toast.error('Fehler beim Bulk-Update');
    }
  };

  // Update local state without re-fetching the full list, so the scroll position
  // and selection state are preserved.
  const patchTodoStatus = (todoId: string, status: TodoStatus) => {
    setActiveTodos((prevActive) => {
      const activeIndex = prevActive.findIndex((t) => t.id === todoId);
      const finalIndex = finalTodos.findIndex((t) => t.id === todoId);

      // Move active -> final
      if (status === 'final' && activeIndex !== -1) {
        const item = { ...prevActive[activeIndex], status };
        setFinalTodos((prevFinal) =>
          [...prevFinal, item].sort(
            (a, b) => new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime()
          )
        );
        return prevActive.filter((t) => t.id !== todoId);
      }

      // Move final -> active
      if (status !== 'final' && finalIndex !== -1) {
        const item = { ...finalTodos[finalIndex], status };
        setFinalTodos((prevFinal) => prevFinal.filter((t) => t.id !== todoId));
        return [...prevActive, item].sort((a, b) => {
          const order = (s: TodoStatus) => (s === 'pending' ? 0 : 1);
          if (order(a.status) !== order(b.status)) return order(a.status) - order(b.status);
          return new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime();
        });
      }

      // Status change within active list (pending <-> done)
      if (activeIndex !== -1) {
        const next = [...prevActive];
        next[activeIndex] = { ...next[activeIndex], status };
        return next.sort((a, b) => {
          const order = (s: TodoStatus) => (s === 'pending' ? 0 : 1);
          if (order(a.status) !== order(b.status)) return order(a.status) - order(b.status);
          return new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime();
        });
      }

      return prevActive;
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllActive = () => {
    if (selectedIds.size === visibleActiveTodos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleActiveTodos.map((t) => t.id)));
    }
  };

  const reportUrl = (report: ReportRow): string => {
    return `${REPORTING_TOOL_URL}/reports/${report.id}`;
  };

  const typeBadge = (reportType: string | null) => {
    if (!reportType || reportType === 'release') return null;
    const labels: Record<string, string> = { tour: 'Tour', follower: 'Follower' };
    const label = labels[reportType] || reportType;
    const colors: Record<string, string> = {
      tour: 'bg-purple-600/20 text-purple-300',
      follower: 'bg-blue-600/20 text-blue-300',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${colors[reportType] || 'bg-gray-600/20 text-gray-300'}`}>
        {label}
      </span>
    );
  };

  const statusBadge = (status: TodoStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1.5 text-yellow-400 text-sm">
            <Clock className="w-3.5 h-3.5" /> Pending
          </span>
        );
      case 'done':
        return (
          <span className="flex items-center gap-1.5 text-green-400 text-sm">
            <Check className="w-3.5 h-3.5" /> Done
          </span>
        );
      case 'final':
        return (
          <span className="flex items-center gap-1.5 text-gray-500 text-sm">
            <Archive className="w-3.5 h-3.5" /> Final
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0000FE]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ListChecks className="w-6 h-6 text-[#0000FE]" />
          <h2 className="text-xl font-bold text-white">Reporting Todo</h2>
          <span className="text-gray-500 text-sm">
            {visibleActiveTodos.filter((t) => t.status === 'pending').length} pending ·{' '}
            {visibleActiveTodos.filter((t) => t.status === 'done').length} done ·{' '}
            {visibleFinalTodos.length} final
          </span>
        </div>
        <button
          onClick={loadTodos}
          className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          title="Aktualisieren"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Campaign type filter */}
      <div className="flex items-center gap-2 mb-5">
        {([
          { id: 'all', label: 'Alle', active: 'bg-[#0000FE] text-white' },
          { id: 'release', label: 'Release', active: 'bg-gray-600 text-white' },
          { id: 'tour', label: 'Tour', active: 'bg-purple-600 text-white' },
          { id: 'follower', label: 'Follower', active: 'bg-blue-600 text-white' },
        ] as const).map((chip) => {
          const count = typeCount(chip.id);
          const selected = typeFilter === chip.id;
          return (
            <button
              key={chip.id}
              onClick={() => {
                setTypeFilter(chip.id);
                // Drop the selection: bulk actions must never hit rows the
                // filter just hid.
                setSelectedIds(new Set());
              }}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selected ? chip.active : 'bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'
              }`}
            >
              {chip.label}
              <span className={`ml-1.5 ${selected ? 'opacity-70' : 'text-gray-600'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 p-3 rounded-lg bg-gray-900 border border-gray-700">
          <span className="text-sm text-gray-300">{selectedIds.size} ausgewählt</span>
          <button
            onClick={() => bulkUpdate('done')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm transition-colors"
          >
            <Check className="w-3.5 h-3.5" /> Als Done
          </button>
          <button
            onClick={() => bulkUpdate('final')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-600 hover:bg-gray-700 text-white text-sm transition-colors"
          >
            <Archive className="w-3.5 h-3.5" /> Als Final
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 rounded-lg hover:bg-gray-800 text-gray-400 text-sm transition-colors"
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* Active list */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#0000FE]"></span> Aktuell
            <span className="text-sm text-gray-500 font-normal">({visibleActiveTodos.length})</span>
          </h3>
          {visibleActiveTodos.length > 0 && (
            <button
              onClick={selectAllActive}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              {selectedIds.size === visibleActiveTodos.length ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
          )}
        </div>

        {visibleActiveTodos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Check className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p>Keine offenen Todos.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleActiveTodos.map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-900 border border-gray-800 hover:border-gray-700 transition-colors"
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleSelect(todo.id)}
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 transition-colors ${
                    selectedIds.has(todo.id)
                      ? 'bg-[#0000FE] border-[#0000FE]'
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  {selectedIds.has(todo.id) && (
                    <Check className="w-3.5 h-3.5 text-white mx-auto" />
                  )}
                </button>

                {/* Report info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium truncate">{todo.report.name}</span>
                    {typeBadge(todo.report.report_type)}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {new Date(todo.report.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                </div>

                {/* Open in reporting tool */}
                <a
                  href={reportUrl(todo.report)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                  title="Im Reporting-Tool öffnen"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>

                {/* Status badge */}
                <div className="flex-shrink-0 w-20">{statusBadge(todo.status)}</div>

                {/* Quick toggle */}
                <button
                  onClick={() => updateStatus(todo.id, todo.status === 'pending' ? 'done' : 'pending')}
                  className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                  title={todo.status === 'pending' ? 'Als Done' : 'Als Pending'}
                >
                  {todo.status === 'pending' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                </button>

                {/* Dropdown menu */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === todo.id ? null : todo.id);
                    }}
                    className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {openMenuId === todo.id && (
                    <div
                      className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg bg-gray-900 border border-gray-700 shadow-xl py-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => updateStatus(todo.id, 'pending')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                      >
                        <Clock className="w-3.5 h-3.5 text-yellow-400" /> Pending
                      </button>
                      <button
                        onClick={() => updateStatus(todo.id, 'done')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                      >
                        <Check className="w-3.5 h-3.5 text-green-400" /> Done
                      </button>
                      <div className="border-t border-gray-800 my-1" />
                      <button
                        onClick={() => updateStatus(todo.id, 'final')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                      >
                        <Archive className="w-3.5 h-3.5 text-gray-400" /> Als Final
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Final / archived list */}
      <div>
        <button
          onClick={() => setShowCompleted(prev => !prev)}
          className="w-full flex items-center gap-2 mb-3 text-left"
          aria-expanded={showCompleted}
        >
          <Archive className="w-4 h-4 text-gray-500" />
          <span className="text-lg font-semibold text-gray-200">Abgeschlossen</span>
          <span className="text-sm text-gray-500 font-normal">({visibleFinalTodos.length})</span>
          <ChevronDown className={`w-4 h-4 ml-auto text-gray-500 transition-transform ${showCompleted ? 'rotate-180' : ''}`} />
        </button>

        {showCompleted && (visibleFinalTodos.length === 0 ? (
          <div className="text-center py-8 text-gray-600 text-sm">
            Noch keine abgeschlossenen Kampagnen.
          </div>
        ) : (
          <div className="space-y-1">
            {visibleFinalTodos.map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-900/50 border border-gray-800/30"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-gray-400 truncate">{todo.report.name}</span>
                  {typeBadge(todo.report.report_type)}
                  <span className="text-xs text-gray-600 ml-2">
                    {new Date(todo.report.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <a
                  href={reportUrl(todo.report)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-white transition-colors"
                  title="Im Reporting-Tool öffnen"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                {statusBadge(todo.status)}
                <button
                  onClick={() => updateStatus(todo.id, 'pending')}
                  className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-white transition-colors"
                  title="Zurück in die Todo-Liste"
                >
                  <Clock className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportingList;
